import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { cotizacionesService } from '@/services/api/cotizaciones.service';
import { ordenesVentaService } from '@/services/api/ordenesVenta.service';
import { notasVentaService } from '@/services/api/notasVenta.service';
import type { ApiResponse } from '@/services/api/api.types';
import { useToast } from '@/contexts/ContextoToast';
import type { Cotizacion } from '@/modelos/documentosComerciales/Cotizacion';
import type { OrdenDeVenta } from '@/modelos/documentosComerciales/OrdenDeVenta';
import type { NotaDeVenta } from '@/modelos/documentosComerciales/NotaDeVenta';

interface DocumentoOrigenGateProps<T extends { estado: string }> {
  cargar: (id: string) => Promise<ApiResponse<T>>;
  estadoRequerido: string;
  mensajeNoEncontrado: string;
  mensajeEstadoInvalido: string;
  rutaRetorno: string;
  validar?: (documento: T) => string | undefined;
  children: (documento: T) => ReactNode;
}

function DocumentoOrigenGate<T extends { estado: string }>({
  cargar,
  estadoRequerido,
  mensajeNoEncontrado,
  mensajeEstadoInvalido,
  rutaRetorno,
  validar,
  children,
}: DocumentoOrigenGateProps<T>) {
  const { documentoId } = useParams<{ documentoId: string }>();
  const navigate = useNavigate();
  const { showError } = useToast();
  const [documento, setDocumento] = useState<T | null>(null);

  useEffect(() => {
    if (!documentoId) return;
    let cancelado = false;

    const rechazar = (mensaje: string) => {
      showError(mensaje);
      navigate(rutaRetorno, { replace: true });
    };

    void cargar(documentoId).then((res) => {
      if (cancelado) return;
      if (!res.ok || !res.data) return rechazar(mensajeNoEncontrado);
      if (res.data.estado !== estadoRequerido) return rechazar(mensajeEstadoInvalido);
      const errorValidacion = validar?.(res.data);
      if (errorValidacion) return rechazar(errorValidacion);
      setDocumento(res.data);
    });

    return () => { cancelado = true; };
  }, [documentoId, cargar, estadoRequerido, mensajeNoEncontrado, mensajeEstadoInvalido, rutaRetorno, validar, navigate, showError]);

  if (!documento) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return <>{children(documento)}</>;
}

const cargarCotizacion = (id: string) => cotizacionesService.getById(id);
const cargarOrdenVenta = (id: string) => ordenesVentaService.getById(id);
const cargarNotaVenta = (id: string) => notasVentaService.getById(id);

const validarNotaVenta = (nota: NotaDeVenta): string | undefined =>
  !nota.clienteId && !nota.clienteNumeroDocumento
    ? 'Esta nota de venta no tiene cliente asignado. Edítela para agregar uno antes de generar el comprobante.'
    : undefined;

export const CotizacionOrigenGate = ({ children }: { children: (cotizacion: Cotizacion) => ReactNode }) => (
  <DocumentoOrigenGate
    cargar={cargarCotizacion}
    estadoRequerido="aceptada"
    mensajeNoEncontrado="No se encontró la cotización"
    mensajeEstadoInvalido="Solo se puede generar un documento desde una cotización aceptada"
    rutaRetorno="/documentos-comerciales?tab=cotizaciones"
  >
    {children}
  </DocumentoOrigenGate>
);

export const OrdenVentaOrigenGate = ({ children }: { children: (orden: OrdenDeVenta) => ReactNode }) => (
  <DocumentoOrigenGate
    cargar={cargarOrdenVenta}
    estadoRequerido="reservada"
    mensajeNoEncontrado="No se encontró la orden de venta"
    mensajeEstadoInvalido="Solo se puede generar un comprobante desde una orden de venta reservada"
    rutaRetorno="/documentos-comerciales?tab=ordenes-venta"
  >
    {children}
  </DocumentoOrigenGate>
);

export const NotaVentaOrigenGate = ({ children }: { children: (nota: NotaDeVenta) => ReactNode }) => (
  <DocumentoOrigenGate
    cargar={cargarNotaVenta}
    estadoRequerido="generada"
    mensajeNoEncontrado="No se encontró la nota de venta"
    mensajeEstadoInvalido="Solo se puede generar un comprobante desde una nota de venta generada"
    rutaRetorno="/documentos-comerciales?tab=notas-venta"
    validar={validarNotaVenta}
  >
    {children}
  </DocumentoOrigenGate>
);
